import React, { useEffect, useRef } from "react";

import CharacterCounter from "../CharacterCounter/CharacterCounter";
import SendButton from "../SendButton/SendButton";

import styles from "../../style/ChatBotWindow.module.css";

interface ChatInputProps {
  input: string;
  setInput: (value: string) => void;
  sendMessage: () => void;
  isLoading: boolean;
  maxLength?: number;
}

/**
 * Component for handling user input with auto-resize and character counting
 */
const ChatInput: React.FC<ChatInputProps> = ({
  input,
  setInput,
  sendMessage,
  isLoading,
  maxLength = 4000,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize textarea auto-height and reset after sending
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Set initial height
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";

      if (input === "") {
        // Reset height after sending a message
        textarea.style.height = "24px";
        textarea.style.overflowY = "hidden";
        if (textarea.parentElement) {
          textarea.parentElement.style.overflowY = "hidden";
        }
      }
    }
  }, [input]);

  return (
    <div className={styles.footer}>
      <div className={styles.inputFrame}>
        {/* Input area with textarea */}
        <div className={styles.inputArea}>
          <textarea
            ref={textareaRef}
            placeholder="Type your message here..."
            className={styles.nativeInput}
            value={input}
            onMouseEnter={() => {
              const textarea = textareaRef.current;
              if (textarea && textarea.scrollHeight > textarea.clientHeight) {
                textarea.classList.add(styles.scrolling);
              }
            }}
            onMouseLeave={() => {
              const textarea = textareaRef.current;
              if (textarea) {
                // Start timer to hide scrollbar on mouse leave
                if (textarea.scrollTimer) {
                  clearTimeout(textarea.scrollTimer);
                }
                textarea.scrollTimer = setTimeout(() => {
                  textarea.classList.remove(styles.scrolling);
                }, 1500);
              }
            }}
            onChange={(e) => {
              setInput(e.target.value);

              // Auto-resize textarea
              e.target.style.height = "auto";
              e.target.style.height =
                Math.min(e.target.scrollHeight, 200) + "px";

              // Add scrollbar only when reaching max height
              if (e.target.scrollHeight > 200) {
                e.target.style.overflowY = "auto";
                // Add scrolling class to show custom scrollbar
                e.target.classList.add(styles.scrolling);
                if (e.target.parentElement) {
                  e.target.parentElement.style.overflowY = "auto";
                }

                // Set a timeout to hide the scrollbar after 1.5 seconds of inactivity
                if (e.target.scrollTimer) {
                  clearTimeout(e.target.scrollTimer);
                }
                e.target.scrollTimer = setTimeout(() => {
                  e.target.classList.remove(styles.scrolling);
                }, 1500);
              } else {
                e.target.style.overflowY = "hidden";
                e.target.classList.remove(styles.scrolling);
                if (e.target.parentElement) {
                  e.target.parentElement.style.overflowY = "hidden";
                }
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            maxLength={maxLength}
          />
        </div>

        {/* Bottom bar with character count and send button */}
        <div className={styles.bottomBar}>
          <CharacterCounter current={input.length} max={maxLength} />
          <SendButton
            disabled={isLoading || !input.trim()}
            onClick={sendMessage}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
